/**
 * Composable SEO de servicio: resuelve el servicio consultado (por ?service=id o
 * por slug semántico) usando el catálogo público ya existente y coordina la
 * inyección de meta tags + JSON-LD. No toca el estado del formulario de reserva
 * ni las peticiones de disponibilidad (una sola lectura opcional del catálogo).
 */
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { findServiceBySlug } from '@/services/seo/serviceSlugMap';
import { useSpecialtiesStore, type Specialty } from '@/stores/specialties.store';
import { applySeoMeta, clearSeoServiceMeta } from '@/services/seo/seoMetaInjector';
import { injectServiceJsonLd, removeServiceJsonLd } from '@/services/seo/seoJsonLd';
import {
  buildCanonicalUrl,
  buildServiceTitle,
  buildServiceDescription,
  buildServiceSeoCopy,
  type SeoService,
  type SeoCopy,
} from '@/services/seo/serviceSeo';

function toSeoService(item: Specialty): SeoService {
  return {
    id: item.id as number,
    name: item.name,
    duration_blocks: item.duration_blocks,
    block_size_minutes: item.block_size_minutes ?? null,
    description: item.description ?? null,
    price: item.price,
  };
}

export function useServiceSeo() {
  const route = useRoute();
  const specialtiesStore = useSpecialtiesStore();

  const service = ref<SeoService | null>(null);
  const copy = ref<SeoCopy | null>(null);

  const queryServiceId = computed<number | null>(() => {
    const id = Number(route.query.service);
    return Number.isInteger(id) && id > 0 ? id : null;
  });

  async function ensureCatalog(): Promise<void> {
    if (specialtiesStore.items.length === 0) {
      await specialtiesStore.fetchAll();
    }
  }

  async function resolveById(id: number): Promise<SeoService | null> {
    await ensureCatalog();
    const found = specialtiesStore.items.find((s) => s.id === id);
    return found ? toSeoService(found) : null;
  }

  async function resolveBySlug(slug: string): Promise<SeoService | null> {
    await ensureCatalog();
    const found = findServiceBySlug(slug, specialtiesStore.items);
    return found ? toSeoService(found) : null;
  }

  async function applyService(svc: SeoService): Promise<void> {
    service.value = svc;
    copy.value = buildServiceSeoCopy(svc);
    applySeoMeta({
      title: buildServiceTitle(svc),
      description: buildServiceDescription(svc),
      canonicalUrl: buildCanonicalUrl(svc),
    });
    injectServiceJsonLd(svc);
  }

  function clearService(): void {
    service.value = null;
    copy.value = null;
    clearSeoServiceMeta();
    removeServiceJsonLd();
  }

  return {
    service,
    copy,
    queryServiceId,
    resolveById,
    resolveBySlug,
    applyService,
    clearService,
  };
}
